import React, { useMemo } from 'react';
import { useExportPageAsPdf } from '@/hooks/useExportPageAsPdf';
import classNames from 'classnames';

import { Button } from '@/components/atoms';
import { DashboardDateRangeSelector } from '@/modules/dashboard/components';
import { PageActionsMenu } from '@/components/molecules/PageActionsMenu';
import { PdfExportOverlay } from '@/components/molecules/PdfExportOverlay';

import { PageInsights } from '../PageInsights';

import { Page, useDashboard, usePage, useWidget, Widget } from '@/modules';
import { useAuth } from '@/modules/auth';
import { config } from '@/config/env';
import { filterWidgetsByTab } from '@/hooks/useFilteredWidgets';
import { PageTab } from '@/modules/pages/types';
import { isDisabledByProducts, isDisabledByIncompleteProducts, getPageDisplayTitle, PAGE_BUILDOUT_EXCLUDED_PRODUCTS } from '@/modules/pages/utils/products';
import { isInternalUser } from '@/modules/widgets/utils/permissions';
interface PageSubHeaderProps {
    title: string;
    onAddWidget: () => void;
    onRestoreDefault?: (content: number[][]) => void;
}

const DashboardPageSubHeader = ({ title, onAddWidget, onRestoreDefault }: PageSubHeaderProps) => {
    const { state: { currentPage, currentPageWidgetsData, currentTab } } = usePage();
    const { state: { dealerContractedProducts } } = useDashboard();
    const { state: { widgets } } = useWidget();
    const { user } = useAuth();
    const exportPageTitle = getPageDisplayTitle(currentPage?.metadata?.['page-titles'], dealerContractedProducts?.contractedProducts ?? []) ?? currentPage?.title;
    const { exportAsPdf, isExporting } = useExportPageAsPdf({ pageTitle: exportPageTitle });

    const tabs = useMemo(() => {
        if (!dealerContractedProducts || !currentPage?.tabs) {
            return [];
        }
        const contractedProducts = (dealerContractedProducts?.contractedProducts || []).map(product => product.toLowerCase());
        const incompleteProducts = dealerContractedProducts?.incompleteProducts ?? [];
        return currentPage?.tabs?.map((tab: PageTab) => {
            const disabled = tab.disabled ? true :
                isDisabledByProducts(tab?.metadata?.products, contractedProducts) ||
                isDisabledByIncompleteProducts(tab?.metadata?.products, incompleteProducts, PAGE_BUILDOUT_EXCLUDED_PRODUCTS);
            return {
                ...tab,
                disabled,
            };
        }).sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
    }, [currentPage?.tabs, dealerContractedProducts]);

    const _page = useMemo(() => {
        const allWidgetsWithData = Object.values(currentPageWidgetsData);
        const filteredWidgets = filterWidgetsByTab(widgets, tabs, currentTab, user);
        const filteredWidgetIds = new Set(filteredWidgets.map(w => w.id));
        const filteredWidgetsWithData = allWidgetsWithData.filter(w => filteredWidgetIds.has(w.id));

        return {
            ...currentPage,
            widgets: filteredWidgetsWithData
        } as Omit<Page, 'widgets'> & { widgets: (Widget & { data: any })[] };
    }, [currentPage, currentPageWidgetsData, widgets, tabs, currentTab, user])

    return (
        <header className={classNames('w-full', 'flex', 'justify-center', 'bg-white', 'shadow-sm', 'relative', 'z-[998]', 'min-h-[70px]', 'sticky', 'top-24', 'left-0')}>
            <div className={classNames('flex flex-wrap px-5', 'container', 'justify-between', 'items-center', 'gap-2', 'py-2')}>
                <div className="flex min-w-0 flex-1 items-center">
                    <h1 
                        className={classNames(
                            'font-roboto font-medium',
                            'text-[clamp(16px,1.5vw,30px)]', // Conservative scaling but original 32px max for big screens 
                            'whitespace-nowrap',
                            'overflow-hidden', 
                            'text-ellipsis', 
                            'min-w-0' 
                        )}
                        title={title} 
                    >
                        {title}
                    </h1>
                    {isExporting && <PdfExportOverlay />}
                </div>
                <div className={classNames('flex gap-8')} data-pdf-hide>
                    <DashboardDateRangeSelector />
                    <div className={classNames('flex gap-2 items-center')}>
                        {config.PAGE_INSIGHTS_ENABLED && user && isInternalUser(user) && (
                            <PageInsights pageWithWidgets={_page}>
                                <Button variant="primary" className="bg-[#2D3A3F] text-white hover:bg-white hover:text-[#2D3A3F] hover:border-[#D3DFE8] border border-transparent font-roboto">Insights</Button>
                            </PageInsights>
                        )}
                        <Button variant="primary" className="bg-[#2D3A3F] text-white hover:bg-white hover:text-[#2D3A3F] hover:border-[#D3DFE8] border border-transparent font-roboto" onClick={onAddWidget}>Add Widget</Button>
                        {currentPage && <PageActionsMenu page={currentPage} exportAsPdf={exportAsPdf} isExporting={isExporting} onRestoreDefault={onRestoreDefault} />}
                    </div>
                </div>
            </div>
        </header>
    );
};

export { DashboardPageSubHeader };
